import { useEffect, useRef, useCallback, useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

interface TurnstileWidgetProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
  /** Called when Turnstile script fails to load within timeout */
  onLoadTimeout?: () => void;
}

const SITE_KEY = '0x4AAAAAACnYGlysDFuK7d6_';
const LOAD_TIMEOUT_MS = 8000;

export function TurnstileWidget({ onVerify, onExpire, onError, onLoadTimeout }: TurnstileWidgetProps) {
  const { t } = useLanguage();
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const renderedRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleError = useCallback(() => {
    onError?.();
    // Fail-open: if timeout handler exists, bypass silently on mobile/network blockers
    if (onLoadTimeout) {
      setHasError(false);
      onLoadTimeout();
      return;
    }
    setHasError(true);
  }, [onError, onLoadTimeout]);

  const renderWidget = useCallback(() => {
    if (!containerRef.current || !window.turnstile) return;
    if (widgetIdRef.current) {
      window.turnstile.remove(widgetIdRef.current);
    }
    renderedRef.current = true;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setHasError(false);
    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: SITE_KEY,
      callback: (token: string) => {
        setHasError(false);
        onVerify(token);
      },
      'expired-callback': onExpire,
      'error-callback': handleError,
      theme: 'auto',
    });
  }, [onVerify, onExpire, handleError]);

  const handleRetry = useCallback(() => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      setHasError(false);
    } else {
      renderWidget();
    }
  }, [renderWidget]);

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      if (!renderedRef.current) {
        console.warn('[Turnstile] Load timeout — bypassing security check');
        if (onLoadTimeout) {
          setHasError(false);
          onLoadTimeout();
        } else {
          setHasError(true);
        }
      }
    }, LOAD_TIMEOUT_MS);

    const existingScript = document.querySelector('script[src*="turnstile"]');
    if (existingScript) {
      if (window.turnstile) {
        renderWidget();
      } else {
        existingScript.addEventListener('load', renderWidget);
      }
      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
        }
      };
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.onload = renderWidget;
    script.onerror = () => {
      if (onLoadTimeout) {
        setHasError(false);
        onLoadTimeout();
      } else {
        setHasError(true);
      }
    };
    document.head.appendChild(script);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
  }, [renderWidget, onLoadTimeout]);

  return (
    <div className="flex flex-col items-center my-2 gap-2">
      <div ref={containerRef} />
      {hasError && (
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4" />
          <span>{t.security.checkFailed}</span>
          <button
            type="button"
            onClick={handleRetry}
            className="inline-flex items-center gap-1 text-secondary hover:underline font-medium"
          >
            <RefreshCw className="h-3 w-3" /> {t.security.retry}
          </button>
        </div>
      )}
    </div>
  );
}
